import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { DATA_QUERY_KEY, Notice, NoticeKind, UnknownDefinition } from '@/core';
import { DataQueryPlugin, DataQueryPluginV2, EventQueryPlugin, QueryPluginContext, VariablePlugin } from '../../model';
import { filterVariableStateMap, getVariableValuesKey } from '../../utils/variable-keys';
import { usePluginQueryContext } from '../common';
import {
  DataQuery,
  getDependsOnFunction,
  getInjectGlobalFiltersFunction,
  getInterpolateVariablesFunction,
  initializeDataQueryPlugin,
} from '../DataQueryPlugins';
import { INTERNAL_GLOBAL_FILTER } from '../GlobalFilters/global-filter-model';
import { usePluginRegistry, usePlugins } from '../plugin-registry';
import { UseDataQueryOptions } from './model';

type ProcessPluginDefinition = {
  definition: UnknownDefinition;
  context: QueryPluginContext;
  plugin: DataQueryPlugin | DataQuery | VariablePlugin | EventQueryPlugin;
};

/**
 * This function processes a plugin definition by interpolating variables and injecting global filters.
 */
export async function processPluginDefinition(args: ProcessPluginDefinition) {
  const { definition, context, plugin } = args;

  const allNotices: Notice[] = [];
  let nextDefinition = definition;

  const interpolateVariables = getInterpolateVariablesFunction(plugin);
  const injectGlobalFilters = getInjectGlobalFiltersFunction(plugin);

  if (interpolateVariables) {
    nextDefinition = produce(nextDefinition, (draft) => {
      draft.spec = interpolateVariables(draft.spec, context);
    });
  }
  if (injectGlobalFilters) {
    try {
      context.globalFilters = context.globalFilters?.filter(
        ({ label, status }) =>
          !context.disabledGlobalFilters?.[(nextDefinition as unknown as { id: string })?.id]?.includes(label) &&
          status !== 'disabled'
      );
      const { spec: injectedSpec, notices = [] } = await injectGlobalFilters(nextDefinition.spec, context);
      nextDefinition = produce(nextDefinition, (draft) => {
        draft.spec = injectedSpec;
      });

      allNotices.push(...notices);
    } catch (e) {
      allNotices.push({
        type: 'error',
        message: `Global filters could not be injected: ${(e as Error).message}`,
        kind: NoticeKind.GlobalFilters,
      });
    }
  } else {
    // If the the global filter is added by a user and the plugin does not support global filters, add a notice
    if (
      context.globalFilters?.length &&
      !context.globalFilters.every((f) => f.statusReason === INTERNAL_GLOBAL_FILTER)
    ) {
      allNotices.push({
        type: 'warning',
        message: `The datasource does not support global filters.`,
        kind: NoticeKind.GlobalFilters,
      });
    }
  }

  return {
    originalDefinition: definition,
    definition: nextDefinition,
    notices: allNotices,
  };
}

/**
 * Walks every loaded plugin's `dependsOn` and unions the variable names they
 * declare. Returns `undefined` when filtering would be unsafe — i.e. there are
 * no definitions, any plugin is still loading, doesn't implement `dependsOn`,
 * or returns a result without an explicit `variables` array. Callers should
 * treat `undefined` as "depend on the full variable state", which mirrors the
 * fallback in `getQueryOptions` in `common.ts` and prevents the cache key
 * from collapsing to an empty filtered state when dependency information is
 * incomplete.
 */
export function collectVariableDependencies(
  definitions: UnknownDefinition[],
  plugins: Array<DataQueryPlugin | DataQueryPluginV2 | undefined>,
  context: QueryPluginContext
): string[] | undefined {
  if (definitions.length === 0) {
    return undefined;
  }
  const allVariableDeps = new Set<string>();
  let canFilter = true;
  for (let i = 0; i < definitions.length; i++) {
    const plugin = plugins[i];
    if (!plugin) {
      canFilter = false;
      continue;
    }
    const dataQueryPlugin = initializeDataQueryPlugin(plugin);
    const dependsOnFn = getDependsOnFunction(dataQueryPlugin);
    if (!dependsOnFn) {
      canFilter = false;
      continue;
    }
    const deps = dependsOnFn(definitions[i]!.spec, context);
    if (!deps?.variables) {
      canFilter = false;
      continue;
    }
    for (const v of deps.variables) {
      allVariableDeps.add(v);
    }
  }
  return canFilter ? [...allVariableDeps] : undefined;
}

/**
 * Helper hook to process multiple data query definitions.
 */
export function useExperimentalProcessQueryDefinitions(
  definitions: UnknownDefinition[],
  options?: UseDataQueryOptions
) {
  const queryContext = usePluginQueryContext();
  const { variableState, globalFilters } = queryContext;
  const { getPlugin } = usePluginRegistry();

  const pluginLoaderResponse = usePlugins(
    DATA_QUERY_KEY,
    definitions.map((d) => ({ kind: d.kind }))
  );

  // Collect variable dependencies from all loaded plugins so we only include
  // relevant variables in the cache key instead of the entire dashboard state.
  // If any plugin is still loading or doesn't implement dependsOn (or omits
  // the `variables` key on its dependsOn result), we fall back to the full
  // variable state to avoid producing a too-narrow key.
  const variableDeps = collectVariableDependencies(
    definitions,
    pluginLoaderResponse.map((r) => r?.data),
    queryContext
  );
  const filteredVarState = filterVariableStateMap(variableState, variableDeps);
  const useHashing = queryContext.featureFlags?.disableVariableStateKeyHashing !== true;
  const variableStateValues = getVariableValuesKey(filteredVarState, useHashing);

  const results = useQuery(['DataQueryProcess', definitions, globalFilters, variableStateValues, options], () => {
    return Promise.all(
      definitions.map(async (definition) => {
        const plugin = await getPlugin(DATA_QUERY_KEY, definition.kind);
        const dataQueryPlugin = initializeDataQueryPlugin(plugin);
        const pluginDefinition = await processPluginDefinition({
          definition: definition,
          context: { ...queryContext, ...options },
          plugin: dataQueryPlugin,
        });

        return pluginDefinition;
      })
    );
  });

  return results;
}
